import React from "react";

// region Libraries
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slider, TextField } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import { CanConsumptionMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Utils
import {
  MetricDescriptor,
  MetricThresholds,
  RANKED_METRICS,
  ScaleMap,
  ThresholdMap,
  alignThresholds,
  formatMetricValue
} from "../can-consumption-metrics";
// endregion Utils
// region Styles
import { ThresholdsContainer } from "../styles";
// endregion Styles

interface AlertThresholdsModalProps {
  open: boolean;
  onClose: () => void;
  scales: ScaleMap;
  thresholds: ThresholdMap;
  onChangeThresholds: (thresholds: ThresholdMap) => void;
  onRestoreDefaults: () => void;
  decimalSeparator: "." | ",";
}

/**
 * Editor of the alert limits, one warning and one critical per indicator.
 *
 * The critical limit is always kept at least as severe as the warning one — and "more severe" points
 * DOWN on km/L and UP on every other indicator.
 */
const AlertThresholdsModal: React.FC<AlertThresholdsModalProps> = ({
  open, onClose, scales, thresholds, onChangeThresholds, onRestoreDefaults, decimalSeparator
}) => {

  const { t } = useTranslation();

  const handleChange = (descriptor: MetricDescriptor, field: keyof MetricThresholds, rawValue: number) => {
    const scale = scales[descriptor.key];

    if (!Number.isFinite(rawValue)) return;

    const bounded = Math.min(scale.max, Math.max(scale.min, rawValue));
    const candidate: MetricThresholds = { ...thresholds[descriptor.key], [field]: bounded };

    onChangeThresholds({ ...thresholds, [descriptor.key]: alignThresholds(descriptor, candidate, field) });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 500 }}>{t(CanConsumptionMessages.limitsTitle)}</h2>
            <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#5f6368", maxWidth: "56ch" }}>
              {t(CanConsumptionMessages.limitsSubtitle)}
            </p>
          </div>
          <IconButton size="small" onClick={onClose} aria-label={t(CanConsumptionMessages.limitsClose)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <ThresholdsContainer>
          {RANKED_METRICS.map((descriptor) => {
            const scale = scales[descriptor.key];
            const current = thresholds[descriptor.key];
            const hint = descriptor.higherIsBetter
              ? t(CanConsumptionMessages.hintHigherIsBetter)
              : t(CanConsumptionMessages.hintLowerIsBetter);
            const rows: { field: keyof MetricThresholds; label: string; swatch: string }[] = [
              { field: "warn", label: t(CanConsumptionMessages.limitsWarn), swatch: "warn" },
              { field: "critical", label: t(CanConsumptionMessages.limitsCritical), swatch: "critical" }
            ];

            return (
              <div className="threshold" key={descriptor.key}>
                <h3>
                  {t(descriptor.shortLabelKey)} <span>{`(${t(descriptor.unitKey)})`}</span>
                </h3>
                <div className="threshold-hint">
                  {`${hint} · ${t(CanConsumptionMessages.limitsFleetMedian, {
                    value: formatMetricValue(scale.median, descriptor.fractionDigits, decimalSeparator)
                  })}`}
                </div>
                {rows.map((row) => (
                  <div className="threshold-row" key={row.field}>
                    <span>
                      <i className={`swatch ${row.swatch}`} />
                      {row.label}
                    </span>
                    <Slider
                      className="slider"
                      min={scale.min}
                      max={scale.max}
                      step={descriptor.step}
                      value={current[row.field]}
                      onChange={(_event, value) => handleChange(descriptor, row.field, Number(value))}
                      aria-label={`${t(descriptor.shortLabelKey)} - ${row.label}`}
                    />
                    <TextField
                      className="number"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={current[row.field]}
                      inputProps={{ min: scale.min, max: scale.max, step: descriptor.step }}
                      onChange={(event) => handleChange(descriptor, row.field, Number(event.target.value))}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </ThresholdsContainer>
      </DialogContent>
      <DialogActions style={{ justifyContent: "space-between" }}>
        <Button onClick={onRestoreDefaults}>{t(CanConsumptionMessages.limitsReset)}</Button>
        <Button variant="contained" color="primary" onClick={onClose}>{t(CanConsumptionMessages.limitsDone)}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertThresholdsModal;
